import React, { useEffect, useRef, useState } from 'react';
import { BookOpen, Check, CheckCircle2, ChevronDown, ChevronRight, Download, FileText, Info, RefreshCw, SlidersHorizontal, X } from 'lucide-react';
import { ReferenceNotes } from '../../architect/MonthlyRecommendationPanel';
import { architectApi, ApiError, MonthlyPricingLibrary, MonthlyQuestion, MonthlyRecommendation, MonthlyRecommendationRequest, PricingMonthlyRow, PricingSheet } from '../../architect/api';
import { monthlyChargeTerms, monthlyExampleLabel, monthlyQuestionLabel, monthlyRowLabel } from '../../architect/monthlyPresentation';
import MonthlyPriceInfo from '../../architect/MonthlyPriceInfo';
import { monthlyCaseScopeBasis } from '../../architect/monthlyCaseScope';
import { invalidatePricingSections } from '../../architect/pricingMath';
import { Sheet } from '../components/Sheet';
import { deliverFile } from '../files';
import { haptic } from '../telegram';
import './tma-monthly-recommendation.css';

interface Props {
  projectId: string;
  sheet: PricingSheet;
  disabled?: boolean;
  expanded?: boolean;
  onExpand?: () => void;
  onCollapse?: () => void;
  onBusyChange?: (busy: boolean) => void;
  onApply: (rows: PricingMonthlyRow[], expectedSheet: string) => boolean;
}

type Detail = 'volumes' | 'library' | 'checks' | null;
const snapshotOf = (sheet: PricingSheet) => JSON.stringify(sheet);
// Review checkmarks are not changes to the agreed quote. Network Apply still
// compares the entire exact sheet before replacing any rows.
const contentSnapshotOf = (sheet: PricingSheet) => JSON.stringify({ ...sheet, plan: sheet.plan ? {
  ...sheet.plan, section_confirmations: undefined,
  ...(sheet.plan.case_pricing ? { case_pricing: sheet.plan.case_pricing.map(value => ({ ...value, section_confirmations: undefined })) } : {}),
} : undefined });
const monthlyTermsSnapshotOf = (sheet: PricingSheet) => JSON.stringify(sheet.monthly.map(row => {
  const terms: unknown[] = row.model === 'simple' ? ['simple', row.simple?.bucket, row.simple?.charges.map(charge => {
    const basis: unknown[] = [charge.ruleId, charge.quantity ?? null, charge.minimum ?? null, charge.manualRate ?? null, charge.unitCost ?? null];
    const scope = monthlyCaseScopeBasis(charge.caseNames);
    if (scope) basis.push(scope);
    return basis;
  })] : row.model === 'fixed' || row.model === 'usage' ? [row.model, row.label, row.amount ?? null,
    row.unit ?? null, row.unit_label ?? null, row.rate ?? null, row.minimum ?? null]
    : [row.label, row.price ?? null, row.overage ?? null];
  if (row.model === 'simple' && (row.displayLabel?.trim() || row.displayDescription?.trim())) {
    terms.push([row.displayLabel?.trim() || null, row.displayDescription?.trim() || null]);
  }
  const scope = monthlyCaseScopeBasis(row.caseNames);
  if (scope) terms.push(scope);
  return terms;
}));
const wholeNumberQuestion = (key: string) => /\.(quantity|minimum)$/.test(key);
const positiveMinimumQuestion = (key: string, required: boolean) => required && key.endsWith('.minimum');
const numericAnswer = (raw: string): number | null => {
  if (!raw.trim()) return null;
  const value = Number(raw);
  return Number.isFinite(value) && value >= 0 && value <= 10_000_000 ? value : null;
};
/** Analyze the approved estimate automatically; applying remains a guarded
 * local draft change. Existing agreements and edits are never overwritten
 * by a late analysis or source check. */
export function TmaMonthlyRecommendation({ projectId, sheet, disabled = false, expanded = true, onExpand, onCollapse, onBusyChange, onApply }: Props) {
  const [recommendation, setRecommendation] = useState<MonthlyRecommendation | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<'analyze' | 'calculate' | 'apply' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [answerDirty, setAnswerDirty] = useState(false);
  const [capturedSnapshot, setCapturedSnapshot] = useState<string | null>(null);
  const [capturedContent, setCapturedContent] = useState<string | null>(null);
  const [appliedSnapshot, setAppliedSnapshot] = useState<string | null>(null);
  const [detail, setDetail] = useState<Detail>(null);
  const [library, setLibrary] = useState<MonthlyPricingLibrary | null>(null);
  const [libraryBusy, setLibraryBusy] = useState(false);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [selectedBenchmark, setSelectedBenchmark] = useState<string | null>(null);
  const capturedSheet = useRef<PricingSheet | null>(null);
  const requestId = useRef(0);
  const answerRevision = useRef(0);
  const autoStarted = useRef<string | null>(null);
  const automaticApplyAttempt = useRef<string | null>(null);
  const libraryRequest = useRef(0);
  const waitTimer = useRef<number | null>(null);
  const wakeWait = useRef<(() => void) | null>(null);
  const snapshot = snapshotOf(sheet);
  const contentSnapshot = contentSnapshotOf(sheet);
  const latest = useRef({ projectId, snapshot, sheet, onApply });
  latest.current = { projectId, snapshot, sheet, onApply };
  const busyCallback = useRef(onBusyChange);
  busyCallback.current = onBusyChange;

  // The native approval action yields to the active sheet, just as it does
  // while the assistant is reading or validating prices.
  useEffect(() => { busyCallback.current?.(!!busy || detail !== null || answerDirty); }, [busy, detail, answerDirty]);
  useEffect(() => () => { busyCallback.current?.(false); }, []);
  useEffect(() => {
    requestId.current += 1;
    autoStarted.current = null;
    automaticApplyAttempt.current = null;
    setRecommendation(null);
    setAnswers({});
    setBusy(null);
    setError(null);
    setAnswerDirty(false);
    setCapturedSnapshot(null);
    setCapturedContent(null);
    setAppliedSnapshot(null);
    setDetail(null);
    capturedSheet.current = null;
    return () => {
      requestId.current += 1;
      if (waitTimer.current !== null) window.clearTimeout(waitTimer.current);
      wakeWait.current?.();
      wakeWait.current = null;
    };
  }, [projectId]);
  useEffect(() => () => { libraryRequest.current += 1; }, []);

  const applied = appliedSnapshot !== null && appliedSnapshot === monthlyTermsSnapshotOf(sheet);
  const stale = capturedContent !== null && capturedContent !== contentSnapshot && !applied;
  const questions = recommendation?.questions ?? [];
  const missingQuestions = questions.filter(q => q.value == null);
  const invalidAnswers = questions.some(q => {
    const raw = answers[q.key] ?? '';
    const parsed = numericAnswer(raw);
    return (q.required || raw.trim() !== '') &&
      (parsed === null || (wholeNumberQuestion(q.key) && !Number.isInteger(parsed)) ||
        (positiveMinimumQuestion(q.key, q.required) && parsed <= 0));
  });
  const numericAnswers = () => {
    const result: Record<string, number> = {};
    questions.forEach(q => {
      const n = numericAnswer(answers[q.key] ?? '');
      if (n !== null) result[q.key] = n;
    });
    return result;
  };

  const recommendationWithWait = async (input: MonthlyRecommendationRequest, id: number, requestProject: string) => {
    for (let attempt = 0; attempt <= 40; attempt += 1) {
      if (id !== requestId.current || latest.current.projectId !== requestProject) return null;
      try { return await architectApi.recommendMonthlyPricing(requestProject, input); }
      catch (err) {
        const apiError = err as ApiError;
        if (apiError.code !== 'recommendation_busy' || attempt === 40) throw err;
        const seconds = Math.min(10, Math.max(1, Number(apiError.body?.retry_after) || 3));
        await new Promise<void>(resolve => {
          wakeWait.current = resolve;
          waitTimer.current = window.setTimeout(() => {
            waitTimer.current = null;
            wakeWait.current = null;
            resolve();
          }, seconds * 1000);
        });
      }
    }
    return null;
  };

  const request = async (recalculate: boolean, tactile = true) => {
    if (disabled || busy || (recalculate && (!recommendation || stale || invalidAnswers))) return;
    if (tactile) haptic.impact('light');
    const requestProject = projectId;
    const sourceSheet = recalculate && capturedSheet.current
      ? capturedSheet.current : JSON.parse(JSON.stringify(latest.current.sheet)) as PricingSheet;
    const sourceSnapshot = snapshotOf(sourceSheet);
    const revision = answerRevision.current;
    const currentRequest = ++requestId.current;
    if (!recalculate) {
      capturedSheet.current = sourceSheet;
      setCapturedSnapshot(sourceSnapshot);
      setCapturedContent(contentSnapshotOf(sourceSheet));
      setAppliedSnapshot(null);
      setRecommendation(null);
      setAnswers({});
      setAnswerDirty(false);
    }
    setBusy(recalculate ? 'calculate' : 'analyze');
    setError(null);
    try {
      const result = await recommendationWithWait({
        current_sheet: sourceSheet,
        ...(recalculate && recommendation ? { analysisToken: recommendation.analysisToken, answers: numericAnswers() } : {}),
      }, currentRequest, requestProject);
      if (!result || currentRequest !== requestId.current || latest.current.projectId !== requestProject) return;
      if (recalculate && revision !== answerRevision.current) {
        setAnswerDirty(true);
        setError('Inputs changed while calculating. Recalculate with the latest values.');
        return;
      }
      setRecommendation(result.recommendation);
      setAnswers(Object.fromEntries(result.recommendation.questions.map(q => [q.key, q.value == null ? '' : String(q.value)])));
      setAnswerDirty(false);
    } catch (err) {
      if (currentRequest === requestId.current && latest.current.projectId === requestProject) {
        setError((err as Error).message || 'Could not prepare the monthly structure. Try again.');
      }
    } finally {
      if (currentRequest === requestId.current) setBusy(null);
    }
  };

  useEffect(() => {
    if (!expanded || disabled || autoStarted.current === projectId) return;
    autoStarted.current = projectId;
    void request(false, false);
    // Start once per project. Editing a local draft must not trigger a model
    // call or replace an already reviewed recommendation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, disabled, expanded]);

  const apply = async () => {
    if (!recommendation?.applyReady || disabled || busy || stale || answerDirty || invalidAnswers || applied ||
      capturedSnapshot === null || !capturedSheet.current) return;
    haptic.impact('medium');
    const expected = latest.current.snapshot;
    const requestProject = projectId;
    const revision = answerRevision.current;
    const currentRequest = ++requestId.current;
    setBusy('apply');
    setError(null);
    try {
      const checked = await recommendationWithWait({
        current_sheet: capturedSheet.current,
        analysisToken: recommendation.analysisToken,
        answers: numericAnswers(),
      }, currentRequest, requestProject);
      if (!checked || currentRequest !== requestId.current || latest.current.projectId !== requestProject) return;
      if (latest.current.snapshot !== expected || answerRevision.current !== revision) {
        setError('Your draft or inputs changed during the check. Refresh the recommendation before applying.');
        return;
      }
      setRecommendation(checked.recommendation);
      if (!checked.recommendation.applyReady) {
        setError('This structure needs another review before it can be applied.');
        return;
      }
      const rows = JSON.parse(JSON.stringify([
        ...(checked.recommendation.preservedBaseline ?? []), ...checked.recommendation.rows,
      ])) as PricingMonthlyRow[];
      if (latest.current.onApply(rows, expected)) {
        setAppliedSnapshot(monthlyTermsSnapshotOf(invalidatePricingSections({ ...latest.current.sheet, monthly: rows }, 'monthly')));
        onCollapse?.();
        haptic.notify('success');
      } else setError('Your draft changed. Refresh the recommendation before replacing it.');
    } catch (err) {
      if (currentRequest === requestId.current && latest.current.projectId === requestProject) {
        setError((err as Error).message || 'The source could not be verified. Refresh the recommendation.');
      }
    } finally {
      if (currentRequest === requestId.current) setBusy(null);
    }
  };

  useEffect(() => {
    if (!recommendation?.applyReady || busy || disabled || stale || answerDirty || invalidAnswers || applied ||
      !capturedSheet.current || capturedSheet.current.monthly.length !== 0 || capturedSnapshot !== snapshot ||
      automaticApplyAttempt.current === recommendation.analysisToken) return;
    automaticApplyAttempt.current = recommendation.analysisToken;
    void apply();
    // Only an untouched, empty monthly draft receives the ready structure
    // automatically. Existing or concurrently edited rows require review.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendation, busy, disabled, stale, answerDirty, invalidAnswers, applied, capturedSnapshot, snapshot]);

  const loadLibrary = async () => {
    if (libraryBusy || library) return;
    const id = ++libraryRequest.current;
    setLibraryBusy(true);
    setLibraryError(null);
    try {
      const result = await architectApi.getMonthlyPricingLibrary();
      if (id === libraryRequest.current) setLibrary(result);
    } catch (err) {
      if (id === libraryRequest.current) setLibraryError((err as Error).message || 'The reference library could not be loaded.');
    } finally {
      if (id === libraryRequest.current) setLibraryBusy(false);
    }
  };
  const openDetail = (next: Detail) => {
    haptic.impact('light');
    setDetail(next);
    if (next === 'library') void loadLibrary();
  };
  const download = async (id: string) => {
    if (downloading) return;
    setDownloading(id);
    setDownloadError(null);
    try { await deliverFile('monthly_benchmark_docx', id); }
    catch (err) { setDownloadError((err as Error).message || 'The reference could not be downloaded. Try again.'); }
    finally { setDownloading(null); }
  };
  const questionField = (q: MonthlyQuestion) => <label className="tma-mr-question" key={q.key}>
    <span>{monthlyQuestionLabel(q, recommendation?.rows ?? [])}{q.required ? <span aria-hidden="true"> *</span> : null}</span>
    <div className="tma-mr-answer"><input className="tma-input" aria-label={monthlyQuestionLabel(q, recommendation?.rows ?? [])} aria-required={q.required} type="number" inputMode={wholeNumberQuestion(q.key) ? 'numeric' : 'decimal'} min={positiveMinimumQuestion(q.key, q.required) ? 1 : 0} max={10_000_000} step={wholeNumberQuestion(q.key) ? 1 : 'any'} value={answers[q.key] ?? ''} placeholder="Enter value" disabled={disabled || busy === 'apply'} onChange={e => {
      if (applied) {
        setCapturedSnapshot(latest.current.snapshot);
        setCapturedContent(contentSnapshotOf(latest.current.sheet));
        setAppliedSnapshot(null);
        automaticApplyAttempt.current = null;
      }
      answerRevision.current += 1;
      setAnswers(current => ({ ...current, [q.key]: e.target.value }));
      setAnswerDirty(true);
    }} /><span>{q.unit}</span></div>
  </label>;
  const recalculateButton = <button type="button" className="tma-btn tma-mr-recalculate" disabled={disabled || !!busy || stale || applied || invalidAnswers || !answerDirty} onClick={() => void request(true)}>
    <RefreshCw size={16} className={busy === 'calculate' ? 'tma-mr-spin' : ''} />{busy === 'calculate' ? 'Calculating…' : 'Recalculate'}
  </button>;
  const rows = recommendation ? [...(recommendation.preservedBaseline ?? []), ...recommendation.rows] : [];
  const selectedDocument = library?.documents.find(doc => doc.id === selectedBenchmark);

  return <section className={'tma-mr' + (expanded ? ' is-expanded' : '')} aria-label="Monthly pricing references" aria-busy={!!busy}>
    <div className="tma-mr-toolbar">
      <button type="button" className="tma-mr-library-open" onClick={() => openDetail('library')}><BookOpen size={16} aria-hidden="true" />Reference library</button>
      {onExpand && (!expanded || onCollapse) ? <button type="button" className="tma-mr-review-toggle" aria-expanded={expanded} disabled={!!busy || answerDirty} onClick={() => expanded ? onCollapse?.() : onExpand()}>{expanded ? 'Close preview' : 'Review prices'}<ChevronDown size={16} aria-hidden="true" /></button> : null}
    </div>
    <div className="tma-mr-review" hidden={!expanded}>
    {busy === 'analyze' ? <div className="tma-mr-loading" role="status"><RefreshCw size={18} className="tma-mr-spin" /><strong>Calculating prices…</strong></div> : null}
    {stale ? <p className="tma-mr-notice" role="status">Prices changed. Refresh to update.</p> : null}
    {error ? <p className="tma-banner err" role="alert">{error}</p> : null}
    {(stale || error) && !busy ? <button type="button" className="tma-btn tma-mr-retry" disabled={disabled} onClick={() => void request(false)}><RefreshCw size={16} />{recommendation ? 'Refresh' : 'Try again'}</button> : null}
    {recommendation ? <div className="tma-mr-result">
      {missingQuestions.length && !applied ? <div className="tma-mr-questions">
        <div className="tma-mr-section"><h4>Confirm volumes</h4></div>
        {missingQuestions.map(questionField)}
        {recalculateButton}
      </div> : null}
      {answerDirty && !missingQuestions.length ? <div className="tma-mr-questions"><p>Recalculate the changed volumes.</p>{recalculateButton}</div> : null}
      {recommendation.preservedBaseline?.length ? <p className="tma-mr-baseline"><Check size={15} />{recommendation.preservedBaseline.length} existing {recommendation.preservedBaseline.length === 1 ? 'item kept' : 'items kept'}</p> : null}
      <div className="tma-mr-terms"><table aria-label="Recommended Simple Structure"><colgroup><col className="tma-mr-item-col" /><col /></colgroup><thead><tr><th scope="col">Service</th><th scope="col">Price</th></tr></thead><tbody>
        {rows.map((row, i) => <tr key={`${row.label}-${i}`}><th scope="row">{monthlyRowLabel(row)}</th><td><div className="tma-mr-price"><div>{row.simple ? row.simple.charges.map(charge => <p key={charge.id}>{monthlyChargeTerms(charge)}</p>) : row.price || 'To confirm'}</div><MonthlyPriceInfo row={row} recommendation={recommendation.rows.includes(row) ? recommendation : null} onOpenReference={id => { setSelectedBenchmark(id); openDetail('library'); }} /></div></td></tr>)}
        <tr className="tma-mr-example"><th scope="row">Example month</th><td>{monthlyExampleLabel(recommendation.exampleMonth)}{recommendation.exampleMonth.total != null && recommendation.exampleMonth.formula ? <details className="tma-mr-calculation"><summary>Calculation</summary><p>{recommendation.exampleMonth.formula}</p></details> : null}</td></tr>
      </tbody></table></div>
      {recommendation.warnings.length ? <button type="button" className={'tma-mr-checks' + (!recommendation.applyReady ? ' needs-review' : '')} onClick={() => openDetail('checks')}><Info size={16} /><span>{recommendation.warnings.length} {recommendation.warnings.length === 1 ? 'item' : 'items'} to review</span><ChevronRight size={16} /></button> : null}
      <div className="tma-mr-tools">
        {questions.length ? <button type="button" onClick={() => openDetail('volumes')}><SlidersHorizontal size={16} />Monthly volumes<ChevronRight size={14} /></button> : null}
      </div>
      <button type="button" className="tma-btn tma-btn-primary tma-mr-main" disabled={disabled || !!busy || stale || answerDirty || invalidAnswers || !recommendation.applyReady || applied} onClick={() => void apply()}>
        {applied ? <CheckCircle2 size={18} /> : busy === 'apply' ? <RefreshCw size={18} className="tma-mr-spin" /> : <Check size={18} />}{applied ? 'Added to draft' : busy === 'apply' ? 'Checking…' : 'Use prices'}
      </button>
    </div> : null}
    </div>

    <Sheet open={detail !== null} onClose={() => { setDetail(null); setSelectedBenchmark(null); }} title={detail === 'volumes' ? 'Monthly volumes' : detail === 'checks' ? 'Pricing checks' : 'Reference library'} className="tma-mr-sheet" headerAction={<button type="button" className="tma-mr-sheet-close" aria-label="Close pricing details" onClick={() => { setDetail(null); setSelectedBenchmark(null); }}><X size={20} /></button>} footer={detail === 'volumes' ? recalculateButton : undefined}>
      {detail === 'volumes' ? <div className="tma-mr-volume-fields">{questions.map(questionField)}{answerDirty ? <p className="tma-mr-hint">Recalculate after changes.</p> : null}{error ? <p className="tma-banner err" role="alert">{error}</p> : null}</div> : null}
      {detail === 'checks' && recommendation ? <div className="tma-mr-evidence">{recommendation.warnings.map((warning, i) => <article key={i}><p>{warning}</p></article>)}</div> : null}
      {detail === 'library' ? <div className="tma-mr-library">

        {libraryBusy ? <div className="tma-mr-loading" role="status"><RefreshCw size={18} className="tma-mr-spin" /><strong>Loading reference estimates…</strong></div> : null}
        {libraryError ? <><p className="tma-banner err" role="alert">{libraryError}</p><button type="button" className="tma-btn tma-mr-retry" onClick={() => void loadLibrary()}><RefreshCw size={16} />Try again</button></> : null}
        {downloadError ? <p className="tma-banner err" role="alert">{downloadError}</p> : null}
        {selectedDocument ? <div className="tma-mr-reference-preview"><button type="button" className="tma-mr-library-back" onClick={() => setSelectedBenchmark(null)}>‹ All reference estimates</button><h4>{selectedDocument.client}</h4><p className="tma-mr-filename">{selectedDocument.filename}</p><div className="tma-mr-reference-rates">{selectedDocument.actualRecommendedSimpleStructure.map((row, i) => <div key={i}><strong>{row.label}</strong><span>{row.price}</span></div>)}</div><ReferenceNotes document={selectedDocument}/><h5>How this structure was derived</h5><p>{selectedDocument.derivationText}</p><button type="button" className="tma-btn tma-mr-reference-download" disabled={!!downloading} onClick={() => void download(selectedDocument.id)}><Download size={17} />{downloading === selectedDocument.id ? 'Preparing download…' : 'Download estimate'}</button></div> : library?.documents.map(doc => <div className="tma-mr-reference" key={doc.id}><button type="button" className="tma-mr-reference-open" onClick={() => setSelectedBenchmark(doc.id)}><span className="tma-mr-reference-icon"><FileText size={20} /></span><span><strong>{doc.client}</strong><small>{doc.filename}</small>{recommendation?.benchmarks.some(source => source.id === doc.id) ? <em><Check size={12} />Used in this plan</em> : null}</span><ChevronRight size={17} /></button><button type="button" className="tma-mr-download-icon" aria-label={`Download ${doc.client} estimate`} disabled={!!downloading} onClick={() => void download(doc.id)}>{downloading === doc.id ? <RefreshCw size={18} className="tma-mr-spin" /> : <Download size={18} />}</button></div>)}
      </div> : null}
    </Sheet>
  </section>;
}
